import PropTypes from "prop-types";
import React, { useContext } from "react";
import { FixedSizeList } from "react-window";

import memoize from "memoize-one";

import { useWindowSize } from "Utilities/Hooks/useWindowSize";
import { isMobileApp } from "Utilities/isMobileApp";

import { BoardContext } from "./BoardContext";
import { Row } from "./Row";
import { BOARD_TYPES } from "./const";
import useSortedRows from "./useSortedRows";

const createItemData = memoize(
  (
    rows,
    columns,
    saveCellValue,
    rowWidth,
    numRowsRenderedPerTick,
    shouldHideTotalColumn,
    wrapperWidth
  ) => ({
    rows,
    columns,
    saveCellValue,
    rowWidth,
    numRowsRenderedPerTick,
    shouldHideTotalColumn,
    wrapperWidth,
  })
);

TableContent.propTypes = {
  rowWidth: PropTypes.number,
  saveCellValue: PropTypes.func,
  innerScrollableRef: PropTypes.object,
  measurements: PropTypes.object,
  shouldHideTotalColumn: PropTypes.bool,
};

export default function TableContent({
  rowWidth,
  saveCellValue,
  innerScrollableRef,
  measurements,
  shouldHideTotalColumn,
}) {
  const windowSize = useWindowSize();
  const [{ columns, rows, sortInfo, filtersApplied, boardType }] =
    useContext(BoardContext);

  const sortedRows = useSortedRows(rows, sortInfo, filtersApplied);

  const isSignupBoard = boardType === BOARD_TYPES.SIGNUP;

  const listHeight = getListHeight(
    isSignupBoard,
    measurements.height,
    rows.length,
    windowSize
  );

  const itemData = createItemData(
    sortedRows,
    columns,
    saveCellValue,
    rowWidth,
    Math.ceil(listHeight / ROW_HEIGHT_IN_PX) + ROW_OVERSCAN_COUNT,
    shouldHideTotalColumn,
    measurements.width
  );

  return (
    <FixedSizeList
      outerRef={innerScrollableRef}
      height={listHeight}
      width={measurements.width}
      itemSize={ROW_HEIGHT_IN_PX}
      itemCount={rows.length}
      itemData={itemData}
      overscanCount={ROW_OVERSCAN_COUNT}
    >
      {Row}
    </FixedSizeList>
  );
}

const ROW_HEIGHT_IN_PX = 40;
const ROW_OVERSCAN_COUNT = 15;

function getListHeight(isSignupBoard, measurementsHeight, numRows, windowSize) {
  // measurementsHeight works until screen width of 840px or less
  // then it stops working, returns a low value, and causes table contents not to show
  if (windowSize.width > 840) {
    return isSignupBoard
      ? windowSize.height - 176 // measurementsHeight isn't working for signup boards, need to use windowSize
      : measurementsHeight - 123;
  }

  // For screens < 840px in width (aka tablets & phones)
  const fullTableHeight = ROW_HEIGHT_IN_PX * numRows + 25; // +25 is a buffer for scroll bars

  // 280 is the approx tablet header height (though depends on board name length/wrapping).
  // 161 is phone header, which leaves the group header on-screen for easier scrolling away from the board
  let headerHeight = windowSize.width > 600 ? 280 : 161;
  if (isSignupBoard) headerHeight -= ROW_HEIGHT_IN_PX; // signup boards don't show top title toolbar area
  // const headerHeight = 0;
  // the Flocknote mobile app (on Apple devices without a physical "home" button) cuts off the bottom totals bar unless we add a buffer
  const footerBuffer = isMobileApp() ? 20 : 0;
  const availableHeightForTable =
    windowSize.height - headerHeight - footerBuffer;

  if (fullTableHeight < availableHeightForTable) {
    // Since these smaller screens end up with vertical scrolling, if the table is small enough
    // we only want to use its natural height, not the full available screen height.
    return fullTableHeight;
  }

  return availableHeightForTable;
}
